import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { SessionService } from "../../../session.service";
import { AuthService } from "../../../auth/auth.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { TwoFactorCodeModalComponent } from "../two-factor-code-modal/two-factor-code-modal.component";

type TargetAction = () => void;

@Component({
  selector: 'app-password-confirmation-modal',
  templateUrl: './password-confirmation-modal.component.html',
  styleUrls: ['./password-confirmation-modal.component.scss']
})
export class PasswordConfirmationModalComponent {
  confirmButtonText: string;
  userName: string;
  targetAction: TargetAction;
  form: FormGroup = new FormGroup({ password: new FormControl('', Validators.required) });
  loading: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { confirmButtonText: string, userName: string, targetAction: TargetAction },
    private dialogRef: MatDialogRef<PasswordConfirmationModalComponent>,
    private session: SessionService,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.confirmButtonText = data.confirmButtonText;
    this.userName = data.userName;
    this.targetAction = data.targetAction;
  }

  login(password: string) {
    return this.auth.login(this.session.getValue('email'), password);
  }

  cancel(): void {
    if (!this.loading) this.dialogRef.close();
  }

  executeTargetAction() {
    if (this.form.invalid) {
      return;
    }

    const password = this.form.get('password')?.value;
    this.authenticate(password);
  }

  private authenticate(password: string): void {
    this.setLoadingState();

    this.login(password)
      .then(() => {
        this.completeAuthenticationAndContinue();
      })
      .catch((errorResponse: HttpErrorResponse) => {
        if (this.isMfaRequired(errorResponse)) {
          const mfaToken = errorResponse.error?.mfaToken;
          if (!mfaToken) {
            this.unsetLoadingState();
            this.snackBar.open('Unable to start two-factor verification. Please try again.', 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar']
            });
            return;
          }

          this.unsetLoadingState();
          this.openTwoFactorCodeModal(mfaToken);
          return;
        }

        this.unsetLoadingState();
        this.snackBar.open(`Login failed. Please check your password and try again.`, 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      });
  }

  private isMfaRequired(errorResponse: HttpErrorResponse): boolean {
    return (
      errorResponse.status === HttpStatusCode.PreconditionRequired ||
      errorResponse.status === HttpStatusCode.PreconditionFailed
    );
  }

  private openTwoFactorCodeModal(mfaToken: string): void {
    const dialogRef = this.dialog.open(TwoFactorCodeModalComponent, {
      width: '400px',
      data: {
        title: 'Two-Factor Authentication',
        description: 'Enter the 6-digit verification code to continue.',
        confirmButtonText: 'Verify'
      }
    });

    dialogRef.afterClosed().subscribe((mfaOtp?: string) => {
      if (!mfaOtp) {
        return;
      }

      this.setLoadingState();
      this.auth.loginWithMfa(mfaToken, mfaOtp)
        .then(() => {
          this.completeAuthenticationAndContinue();
        })
        .catch(() => {
          this.unsetLoadingState();
          this.snackBar.open('An error occurred during MFA verification. Please try again.', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        });
    });
  }

  private completeAuthenticationAndContinue(): void {
    this.unsetLoadingState();
    this.dialogRef.close();
    this.targetAction();
  }

  private setLoadingState() {
    this.loading = true;
  }

  private unsetLoadingState() {
    this.loading = false;
  }
}
